import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { Box, Typography, Grid, Card, CardContent, CardMedia, CircularProgress, IconButton } from '@mui/material';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import MessageIcon from '@mui/icons-material/Message';
import BottomNavigationBar from './BottomNavigationBar';

const MatchesScreen = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  useEffect(() => {
    const fetchMutualMatches = async () => {
      if (!currentUser) {
        setLoading(false);
        navigate('/signin');
        return;
      }
      
      const currentUserId = currentUser.uid;
      const matchedUsers = [];

      try {
        // Step 1: Get all users the current user (A) has liked (Target B)
        const myLikesQuery = query(
          collection(db, 'matches'),
          where('userId', '==', currentUserId),
          where('action', '==', 'like')
        );
        const myLikesSnapshot = await getDocs(myLikesQuery);
        const usersILikedIds = myLikesSnapshot.docs.map(doc => doc.data().targetUserId);
        
        // Short-circuit if no likes were made
        if (usersILikedIds.length === 0) {
            setMatches([]);
            setLoading(false);
            return;
        }

        // Step 2: Check if those target users (B) have also liked the current user (A)
        // We only need to check matches where the target is the current user.
        const matchesQuery = query(
            collection(db, 'matches'),
            where('targetUserId', '==', currentUserId),
            where('action', '==', 'like')
        );
        const matchesSnapshot = await getDocs(matchesQuery);

        // Step 3: Find the mutual matches
        for (const docSnapshot of matchesSnapshot.docs) {
          const matchedData = docSnapshot.data();
          const likerId = matchedData.userId; // The ID of the user (B) who liked me (A)
          
          // Check if this liker (B) is also someone I (A) liked
          if (usersILikedIds.includes(likerId)) {
            // We have a mutual match! Fetch their user data.
            const userDoc = await getDoc(doc(db, 'users', likerId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              matchedUsers.push({
                id: likerId,
                ...userData,
                profilePictureUrl: userData.profilePhoto || 'https://via.placeholder.com/150',
              });
            }
          }
        }

        setMatches(matchedUsers);

      } catch (error) {
        console.error('Error fetching mutual matches:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMutualMatches();
  }, [currentUser, navigate]);


  const handleMessageMatch = async (matchedUserId) => {
    // 1. Determine the Chat ID (e.g., sort UIDs and join them)
    const chatIds = [currentUser.uid, matchedUserId].sort();
    const chatId = chatIds.join('_');

    // 2. Check if chat document already exists in 'chats' collection
    const chatDocRef = doc(db, 'chats', chatId);
    const chatDoc = await getDoc(chatDocRef);

    if (!chatDoc.exists()) {
      // 3. If chat doesn't exist, create it
      await setDoc(chatDocRef, {
        participants: chatIds,
        lastMessage: 'Say hello!',
        lastMessageTime: new Date(),
        // You may need to add unread counts or other initial fields here
      });
    }

    // 4. Navigate to the chat screen
    navigate(`/chat/${chatId}`);
  };


  if (loading) {
    return (
      <Box sx={styles.loader}>
        <CircularProgress size={40} sx={{ color: '#C00068' }} />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography variant="h5" sx={styles.headerTitle}>
          Your Matches
        </Typography>
      </Box>

      <Box sx={styles.content}>
        {matches.length > 0 ? (
          <Grid container spacing={2}>
            {matches.map((match) => (
              <Grid item xs={6} sm={4} md={3} key={match.id}>
                <Card sx={styles.matchCard}>
                  <CardMedia
                    component="img"
                    sx={styles.cardMedia}
                    image={match.profilePictureUrl}
                    alt={match.firstName}
                    onClick={() => navigate(`/profile/${match.id}`)} // Placeholder navigation
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/150';
                    }}
                  />
                  <CardContent sx={styles.cardContent}>
                    <Typography variant="subtitle1" sx={styles.matchName} noWrap>
                      {match.firstName}
                    </Typography>
                    <Box sx={styles.actionRow}>
                      <IconButton
                        size="small"
                        sx={styles.messageButton}
                        onClick={() => handleMessageMatch(match.id)}
                      >
                        <MessageIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                      <IconButton 
                        size="small"
                        sx={styles.likeIcon}
                        disabled
                      >
                        <FavoriteIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={styles.emptyState}>
            <FavoriteIcon sx={{ fontSize: 60, color: '#C00068', mb: 2 }} />
            <Typography variant="h6" sx={styles.emptyText}>
              No Matches Yet!
            </Typography>
            <Typography sx={styles.emptySubtext}>
              Keep swiping to find someone who likes you back.
            </Typography>
          </Box>
        )}
      </Box>

      <BottomNavigationBar />
    </Box>
  );
};

const styles = {
  container: {
    flex: 1,
    minHeight: '100vh',
    pb: { xs: '60px', md: 0 },
    display: 'flex',
    flexDirection: 'column',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  header: {
    background: 'linear-gradient(to right, #B200BB, #C00068)',
    padding: '16px',
    display: 'flex',
    justifyContent: 'center',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
  },
  headerTitle: {
    color: '#FFFFFF',
    fontWeight: 'bold',
    fontSize: '24px',
  },
  content: {
    flex: 1,
    padding: '16px',
    overflowY: 'auto',
  },
  matchCard: {
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
    },
  },
  cardMedia: {
    height: 150,
    objectFit: 'cover',
    cursor: 'pointer',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
  },
  cardContent: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  matchName: {
    fontWeight: 'bold',
    color: '#333',
    fontSize: '16px',
    mb: 1,
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
  },
  messageButton: {
    backgroundColor: '#C00068',
    color: '#FFF',
    '&:hover': {
      backgroundColor: '#9A0054',
    },
  },
  likeIcon: {
    color: '#C00068',
    opacity: 0.7,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    textAlign: 'center',
  },
  emptyText: {
    fontWeight: 'bold',
    color: '#C00068',
  },
  emptySubtext: {
    color: '#888',
    mt: 1,
  }
};

export default MatchesScreen;